import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { LprForloebEditor } from '@/components/lpr/LprForloebEditor';
import {
  mapReferrerTypeToHenvisningsmaade,
  useCreateLprForloeb,
  useLprForloebForPatient,
  useLprSettings,
  useUpsertLprSettings,
} from '@/hooks/useLpr';
import type { ReferralListItem } from '@/hooks/useReferrals';
import type { LprForloeb } from '@/types/lpr';

export function ReferralLprTab({ referral }: { referral: ReferralListItem }) {
  const { t } = useTranslation();
  const create = useCreateLprForloeb();
  const autoStarted = useRef(false);
  const [created, setCreated] = useState<LprForloeb | null>(null);
  const { data: forloebs, isLoading } = useLprForloebForPatient(
    referral.clinic_id,
    referral.patient_id,
  );

  const linked =
    created ?? forloebs?.find((f) => f.referral_id === referral.id) ?? null;
  const patientName = `${referral.first_name} ${referral.last_name}`.trim();

  async function handleCreate() {
    if (!referral.patient_id) return;
    try {
      const row = await create.mutateAsync({
        clinic_id: referral.clinic_id,
        patient_id: referral.patient_id,
        referral_id: referral.id,
        henvisningstidspunkt: referral.received_at,
        henvisningsmaade: mapReferrerTypeToHenvisningsmaade(referral.referrer_type),
        importAppointments: true,
      });
      setCreated(row);
    } catch (err) {
      console.error('create lpr from referral failed', err);
      toast.error(t('lpr.save_error'));
      autoStarted.current = false;
    }
  }

  useEffect(() => {
    if (!referral.patient_id || isLoading || linked || create.isPending || autoStarted.current) {
      return;
    }
    autoStarted.current = true;
    void handleCreate();
  }, [referral.patient_id, isLoading, linked, create.isPending]);

  if (!referral.patient_id) {
    return (
      <section className="rounded-lg border border-ds-line bg-ds-card p-12 text-center shadow-card">
        <h2 className="text-sm font-medium text-ds-text">{t('lpr.needs_patient_title')}</h2>
        <p className="mt-2 text-sm text-ds-muted">{t('lpr.needs_patient_description')}</p>
      </section>
    );
  }

  if (isLoading || (!linked && (create.isPending || autoStarted.current))) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
      </div>
    );
  }

  if (!linked) {
    return (
      <section className="rounded-lg border border-ds-line bg-ds-card p-12 text-center shadow-card">
        <h2 className="text-sm font-medium text-ds-text">{t('lpr.no_forloeb_title')}</h2>
        <p className="mt-2 text-sm text-ds-muted">{t('lpr.no_forloeb_from_referral')}</p>
        <p className="mt-1 text-sm text-ds-muted">
          {t('lpr.patient_line', { name: patientName })}
        </p>
        <div className="mt-4">
          <Button onClick={() => void handleCreate()} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('lpr.create_from_referral')}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-ds-text">
          {t('lpr.patient_line', { name: patientName })}
        </p>
        <p className="mt-1 text-sm text-ds-muted">{t('lpr.tab_help')}</p>
      </div>
      <ClinicSorField clinicId={referral.clinic_id} />
      <LprForloebEditor forloeb={linked} />
    </div>
  );
}

function ClinicSorField({ clinicId }: { clinicId: string }) {
  const { t } = useTranslation();
  const { data: settings } = useLprSettings(clinicId);
  const upsert = useUpsertLprSettings(clinicId);

  return (
    <div className="max-w-md">
      <Input
        label={t('lpr.clinic_sor')}
        defaultValue={settings?.sor_id ?? ''}
        key={settings?.sor_id ?? 'empty'}
        onBlur={(e) => {
          const next = e.target.value.trim() || null;
          if (next === (settings?.sor_id ?? null)) return;
          void upsert.mutateAsync({ sor_id: next }).then(
            () => toast.success(t('lpr.saved')),
            (err) => {
              console.error('save clinic SOR failed', err);
              toast.error(t('lpr.save_error'));
            },
          );
        }}
      />
    </div>
  );
}
